<template>
  <div class="content">
    <!-- Wybór liczby użytkowników na stronie (10/20/50) — ten sam mechanizm,
         co na liście pojazdów (`VehicleList`). -->
    <div v-if="!edit" class="user-list-toolbar">
      <!-- Tworzenie grupy poszukiwanych (czarna/biała lista) — tylko SG/NOSG/POSG.
           Użytkownicy zaznaczeni w tabeli trafiają do okna jako wstępnie wybrani. -->
      <button
        v-if="isSG"
        type="button"
        class="btn button-add-user user-list-create-group"
        @click="showCreateGroup = true"
      >
        {{ t('create-user-group') }}
      </button>
      <label class="user-list-page-size-label" for="user-list-page-size">
        {{ t('users-per-page') }}
      </label>
      <select
        id="user-list-page-size"
        class="user-list-page-size-select"
        :value="pageSize"
        @change="onPageSizeChange"
      >
        <option v-for="option in pageSizeOptions" :key="option" :value="option">
          {{ option }}
        </option>
      </select>
    </div>
    <!-- Tabela z listą użytkowników (tylko jeśli nie jesteśmy w trybie edycji).
         Zestaw kolumn zależy od wariantu wdrożenia: warianty SG (SG/NOSG/POSG)
         dokładają „Grupy poszukiwanych" i „Jednostkę".
         Przy komplecie kolumn tabela schodzi na mniejszy stopień typografii —
         wiersz ma się zmieścić w jednej linii. Szerokości rozdaje odpowiednia
         klasa `user-table--*`. -->
    <table
      class="user-table table table-striped"
      :class="tableVariantClass"
      v-if="!edit"
    >
      <thead class="thead-light">
        <tr>
          <!-- Nagłówki kolumn. Szerokości pochodzą z klas `cell-*` (CSS),
               nie z atrybutów `style` — inaczej trzeba by je trzymać w dwóch
               zestawach w szablonie. -->
          <th class="cell-lp">{{ t('ordinal-number') }}</th>
          <!-- Kolumny z danymi są sortowalne kliknięciem nagłówka (tylko SG/NOSG/POSG);
               ponowne kliknięcie odwraca kierunek. -->
          <th
            v-for="column in dataColumns"
            :key="column.key"
            :class="[column.cellClass, { 'cell-sortable': isSG, 'cell-sorted': isSG && sortKey === column.key }]"
            :title="isSG ? t('sort-by-column') : null"
            :aria-sort="ariaSort(column.key)"
            @click="toggleSort(column.key)"
          >
            {{ t(column.label) }}
            <span v-if="isSG" class="sort-indicator" aria-hidden="true">{{ sortIndicator(column.key) }}</span>
          </th>
          <th v-if="isSG" class="cell-unit">{{ t('unit') }}</th>
          <th class="cell-actions">{{ t('action') }}</th>
          <th class="cell-select"></th>
        </tr>
      </thead>
      <tbody>
        <!-- Pętla po użytkownikach wyświetlanych na danej stronie.
             `title` na kolumnach, które mogą zostać przycięte wielokropkiem —
             pełna wartość jest wtedy dostępna w podpowiedzi. -->
        <tr v-for="(users, index) in paginatedUsers" :key="users.id" class="users-list">
          <td class="cell-lp">{{ (pageNumber - 1) * pageSize + index + 1 }}</td>
          <td class="cell-name" :title="users.username">{{ users.username }}</td>
          <td class="cell-roles" :title="rolesLabel(users)">
            <!-- Zwraca uprawnienia użytkownika (role jako tekst) -->
            {{ rolesLabel(users) }}
          </td>
          <td class="cell-ugroup" :title="userGroupLabel(users)">
            <!-- Grupa użytkowników (opcjonalna, zarządzana w zakładce „Grupy użytkowników") -->
            {{ userGroupLabel(users) }}
          </td>
          <td v-if="isSG" class="cell-groups" :title="wantedGroupsLabel(users)">
            <!-- Grupy list poszukiwanych, do których user ma dostęp (członek lub właściciel) -->
            {{ wantedGroupsLabel(users) }}
          </td>
          <td class="cell-login">
            <!-- Data ostatniego logowania, sformatowana -->
            {{ users.lastLogin ? moment(users.lastLogin).format("YYYY-MM-DD HH:mm:ss") : "" }}
          </td>
          <td class="cell-firstname" :title="users.firstName">{{ users.firstName }}</td>
          <td class="cell-lastname" :title="users.lastName">{{ users.lastName }}</td>
          <!-- „Jednostka" — kolumna bez źródła danych; API nie zwraca jeszcze
               tej informacji, więc do czasu dodania pola zostaje pusta. -->
          <td v-if="isSG" class="cell-unit"></td>
          <td class="cell-actions">
            <!-- Oba przyciski w jednym kontenerze: odstęp robi `gap`, a
                 `flex-wrap: nowrap` gwarantuje, że nigdy nie ustawią się jeden
                 pod drugim (przy dłuższych tłumaczeniach etykiet, np. ro
                 „DECONECTARE", przycina je `overflow` komórki). -->
            <div class="user-actions">
              <!-- Przycisk do przejścia w tryb edycji wybranego użytkownika -->
              <button
                type="button"
                @click="setToEdit(users.id)"
                :disabled="!canEditUser(users)"
                :title="lockedActionTitle(users, canEditUser(users))"
                class="btn button-edit"
              >
                {{ t('edit') }}
              </button>
              <!-- Przycisk wymuszający wylogowanie (usunięcie sesji użytkownika z bazy) -->
              <button
                type="button"
                @click="killSession(users.id, users.username)"
                :disabled="!canKillSession(users)"
                :title="killSessionTitle(users)"
                class="btn button-add-user button-force-logout"
              >
                {{ t('kill-session') }}
              </button>
            </div>
          </td>
          <td class="cell-select">
            <!-- Checkbox do zaznaczania użytkownika do usunięcia -->
            <input-checkbox
              @click="setToDelete(users.id)"
              :checked-condition="toDelete.includes(users.id)"
              :disabled="!canAdministerUser(users)"
            ></input-checkbox>
          </td>
        </tr>
      </tbody>
    </table>
    <!-- Panel paginacji i przycisk usuwania zaznaczonych użytkowników (gdy nie w trybie edycji) -->
    <div class="actions" v-if="!edit">
      <PagePanel
        :page-num="pageNumber"
        :page-size="pageSize"
        :total-size="totalCount"
        v-on:setPage="setPage"
      />
      <button
        type="button"
        @click="deleteUsers"
        :disabled="toDelete.length === 0"
        class="remove btn button-delete"
      >
        <span>{{ t('delete') }}</span>
      </button>
    </div>
    <CreateWantedGroupModal
      v-if="isSG"
      :is-visible="showCreateGroup"
      :users="users"
      :preselected-ids="toDelete"
      @close="showCreateGroup = false"
      @created="handleGroupCreated"
    />
    <!-- Komponent formularza edycji użytkownika (po kliknięciu przycisku edytuj) -->
    <div class="content" v-if="edit">
      <EditUser :user-id="toEdit" v-on:goBack="handleGoBack" />
    </div>
  </div>
</template>

<script>
// Importujemy narzędzia i komponenty
import moment from "moment"; // Do formatowania dat
import Users from "../services/Users"; // Serwis pobierania użytkowników z backendu
import InputCheckbox from "@/components/InputCheckbox"; // Własny komponent checkboxa
import EditUser from "@/components/EditUser"; // Komponent do edycji użytkownika
import PagePanel from "@/components/table/PagePanel"; // Komponent paginacji
import CreateWantedGroupModal from "@/components/users/CreateWantedGroupModal"; // Tworzenie grupy poszukiwanych
import { isSG } from "@/config/appVersion"; // Wariant wdrożenia (SG / NOSG / POSG)
import { useNotificationStore } from "@/stores/notification.store"; // Globalne powiadomienia
import { useUserStore } from "@/stores/user.store"; // Hierarchia uprawnień (kogo wolno edytować/usuwać)

moment.locale("pl"); // Ustawienie polskiego locale

export default {
  components: { PagePanel, EditUser, InputCheckbox, CreateWantedGroupModal }, // Deklarujemy używane komponenty
  name: "UserList",
  data() {
    return {
      loading: false,         // Czy trwa ładowanie użytkowników
      users: [],              // Lista użytkowników z backendu (wszystkich)
      filteredUsers: [],      // Lista użytkowników po filtracji (np. po wyszukiwaniu)
      pageNumber: 1,          // Numer bieżącej strony (dla paginacji)
      pageSize: 10,           // Liczba użytkowników na stronie (jedna z `pageSizeOptions`)
      pageSizeOptions: [10, 20, 50], // Dozwolone rozmiary strony
      totalCount: 10,         // Łączna liczba użytkowników
      error: null,            // Pole na błąd
      moment: moment,         // Instancja moment.js
      dataService: Users,     // Serwis komunikacji z backendem
      toDelete: [],           // Tablica ID użytkowników zaznaczonych do usunięcia
      edit: false,            // Czy jesteśmy w trybie edycji użytkownika
      toEdit: null,           // ID użytkownika aktualnie edytowanego
      sortKey: "username",    // Kolumna sortowania (klucz z `dataColumns`)
      sortDirection: "asc",   // Kierunek sortowania: 'asc' | 'desc'
      showCreateGroup: false, // Czy okno tworzenia grupy poszukiwanych jest otwarte
    };
  },
  props: {
    searchQuery: {
      type: String,
      default: '',
    },
    // Identyfikator wybranej grupy list poszukiwanych do filtrowania ('' = wszystkie).
    wantedGroupId: {
      type: [String, Number],
      default: '',
    },
  },
  watch: {
    // Reaguje na zmianę zapytania wyszukiwania
    searchQuery() {
      this.applyFilters(true);
    },
    // Reaguje na zmianę wybranej grupy list poszukiwanych
    wantedGroupId() {
      this.applyFilters(true);
    },
  },
  created() {
    // Automatycznie pobierz listę użytkowników po załadowaniu komponentu
    this.retrieveUsers();
  },
  setup() {
    // Kolumny „Grupy poszukiwanych" i „Jednostka", sortowanie po nagłówkach oraz tworzenie
    // grup poszukiwanych — tylko w wariantach SG (SG/NOSG/POSG).
    // `userStore` daje hierarchię: administrator widzi konta równorzędnych administratorów,
    // ale ich nie edytuje, nie usuwa i nie wylogowuje (backend zwraca na to 403).
    return { isSG, userStore: useUserStore() };
  },
  computed: {
    // Klasa z szerokościami kolumn dobrana do zestawu kolumn danego wariantu:
    // SG/NOSG/POSG (grupy + jednostka), pozostałe (żadna z nich).
    tableVariantClass() {
      return this.isSG ? "user-table--sg" : "user-table--plain";
    },
    // Kolumny z danymi (między „Lp." a „Jednostką") w kolejności wyświetlania. `key` to
    // klucz sortowania ({@see sortValue}); „Grupy poszukiwanych" tylko w wariantach SG.
    dataColumns() {
      const columns = [
        { key: "username", label: "name2", cellClass: "cell-name" },
        { key: "roles", label: "permissions", cellClass: "cell-roles" },
        { key: "userGroup", label: "user-group", cellClass: "cell-ugroup" },
        { key: "wantedGroups", label: "wanted-list-groups", cellClass: "cell-groups", sgOnly: true },
        { key: "lastLogin", label: "last-login", cellClass: "cell-login" },
        { key: "firstName", label: "name", cellClass: "cell-firstname" },
        { key: "lastName", label: "lastname", cellClass: "cell-lastname" },
      ];

      return columns.filter((column) => !column.sgOnly || this.isSG);
    },
    // Liczba stron dla aktualnie przefiltrowanej listy. Minimum 1 — pusta lista
    // to nadal „strona 1 z 1", a nie „strona 1 z 0".
    totalPages() {
      return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
    },
    // Zwraca użytkowników wyświetlanych na bieżącej stronie (paginacja)
    paginatedUsers() {
      const startIndex = (this.pageNumber - 1) * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      return this.filteredUsers.slice(startIndex, endIndex);
    },
  },
  methods: {
    // Uprawnienia (role) użytkownika jako jeden tekst — używane i w komórce,
    // i w `title`, więc trzymamy to w jednym miejscu.
    rolesLabel(user) {
      return user.roles ? user.roles.map((it) => it.name).join(" ") : "";
    },
    // Czy zalogowany może wejść w edycję tego konta (własne — tak, równorzędny admin — nie).
    canEditUser(user) {
      return this.userStore.canEditUser(user);
    },
    // Czy zalogowany może usunąć to konto lub wymusić jego wylogowanie. Własne konto NIE
    // wchodzi w grę — administrator nie odcina sam sobie dostępu.
    canAdministerUser(user) {
      return this.userStore.canAdministerUser(user);
    },
    // Podpowiedź przy zablokowanym przycisku — inaczej wygaszenie wygląda na usterkę.
    lockedActionTitle(user, allowed) {
      return allowed ? "" : this.t("user-out-of-your-hierarchy");
    },
    // Czy konto ma w tej chwili sesję w systemie (pole `logged` z API).
    isUserLogged(user) {
      return user.logged === true;
    },
    // Wymuszone wylogowanie ma sens tylko wobec konta, które faktycznie jest zalogowane —
    // przy braku sesji backend i tak nic nie robi, więc przycisk zostaje wygaszony.
    canKillSession(user) {
      return this.canAdministerUser(user) && this.isUserLogged(user);
    },
    // Podpowiedź dla przycisku „Wyloguj": osobny komunikat dla hierarchii uprawnień,
    // osobny dla konta bez sesji — inaczej nie wiadomo, dlaczego przycisk jest nieaktywny.
    killSessionTitle(user) {
      if (!this.canAdministerUser(user)) {
        return this.t("user-out-of-your-hierarchy");
      }

      if (!this.isUserLogged(user)) {
        return this.t("user-not-logged");
      }

      return "";
    },
    // Nazwa grupy użytkowników (pusta, gdy użytkownik nie należy do żadnej grupy).
    userGroupLabel(user) {
      return user.userGroup?.name || "";
    },
    // Grupy list poszukiwanych użytkownika jako jeden tekst (kolumna POSG).
    wantedGroupsLabel(user) {
      return (user.wantedListContactGroups || []).map((g) => g.name).join(", ");
    },
    // Wartość komórki, po której sortujemy. Daty jako znacznik czasu (null = nigdy nie
    // logował się), reszta jako tekst.
    sortValue(user, key) {
      switch (key) {
        case "roles":
          return this.rolesLabel(user);
        case "userGroup":
          return this.userGroupLabel(user);
        case "wantedGroups":
          return this.wantedGroupsLabel(user);
        case "lastLogin":
          return user.lastLogin ? moment(user.lastLogin).valueOf() : null;
        default:
          return user[key] || "";
      }
    },
    // Porównanie dwóch kont wg bieżącej kolumny i kierunku. Puste wartości zawsze na końcu
    // (niezależnie od kierunku) — konto bez logowania czy bez grupy nie ma przesłaniać reszty.
    // Remis rozstrzyga login, żeby kolejność była stabilna między odświeżeniami.
    compareUsers(a, b) {
      const direction = this.sortDirection === "desc" ? -1 : 1;
      const left = this.sortValue(a, this.sortKey);
      const right = this.sortValue(b, this.sortKey);
      const leftEmpty = left === null || left === "";
      const rightEmpty = right === null || right === "";

      if (leftEmpty !== rightEmpty) {
        return leftEmpty ? 1 : -1;
      }

      let result = 0;
      if (!leftEmpty) {
        result = typeof left === "number" && typeof right === "number"
          ? left - right
          : String(left).localeCompare(String(right), "pl", { sensitivity: "base", numeric: true });
      }

      if (result === 0 && this.sortKey !== "username") {
        return (a.username || "").localeCompare(b.username || "", "pl", { sensitivity: "base" });
      }

      return result * direction;
    },
    // Klik w nagłówek: ta sama kolumna → odwrócenie kierunku, inna → rosnąco. Poza wariantami
    // SG nagłówki nie są klikalne (lista zostaje posortowana po loginie).
    toggleSort(key) {
      if (!this.isSG) {
        return;
      }

      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortDirection = "asc";
      }

      this.applyFilters(true);
    },
    sortIndicator(key) {
      if (this.sortKey !== key) {
        return "↕";
      }

      return this.sortDirection === "asc" ? "▲" : "▼";
    },
    ariaSort(key) {
      if (!this.isSG || this.sortKey !== key) {
        return null;
      }

      return this.sortDirection === "asc" ? "ascending" : "descending";
    },
    // Po utworzeniu grupy odświeżamy listę — kolumna „Grupy poszukiwanych" i filtr grup
    // mają od razu pokazać nową grupę u przypiętych użytkowników.
    handleGroupCreated() {
      this.showCreateGroup = false;
      this.toDelete = [];
      this.refreshData();
    },
    // Frazy wyszukiwania rozbijamy na słowa — „kowalski jan" ma znaleźć konto,
    // w którym jedno słowo pasuje do nazwiska, a drugie do imienia. Każde słowo
    // musi trafić w co najmniej jedno z pól: login, imię, nazwisko.
    matchesSearch(user, terms) {
      if (terms.length === 0) {
        return true;
      }

      const haystack = [user.username, user.firstName, user.lastName]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return terms.every((term) => haystack.includes(term));
    },
    // Filtrowanie użytkowników po loginie, imieniu i nazwisku (case-insensitive)
    // ORAZ po przynależności do wybranej grupy list poszukiwanych. `resetPage` = true
    // przy zmianie kryteriów filtra, false przy zwykłym odświeżeniu danych
    // (np. zmiana strony), żeby nie gubić bieżącej strony.
    applyFilters(resetPage = false) {
      const terms = (this.searchQuery || "").toLowerCase().trim().split(/\s+/).filter(Boolean);
      const groupId = this.wantedGroupId === "" || this.wantedGroupId == null
        ? null
        : Number(this.wantedGroupId);

      this.filteredUsers = this.users.filter((user) => {
        // Warunek: login, imię lub nazwisko zawierają szukaną frazę.
        const matchesQuery = this.matchesSearch(user, terms);

        // Warunek: user należy do wybranej grupy list poszukiwanych (członek lub właściciel).
        const matchesGroup =
          groupId === null ||
          (user.wantedListContactGroups || []).some((g) => g.id === groupId);

        return matchesQuery && matchesGroup;
      });

      // Sortowanie po wybranej kolumnie (domyślnie login rosnąco) — przed paginacją klientową.
      this.filteredUsers.sort(this.compareUsers);

      // Paginacja klientowa odzwierciedla liczbę przefiltrowanych rekordów.
      this.totalCount = this.filteredUsers.length;

      if (resetPage) {
        this.pageNumber = 1; // Po zmianie kryteriów filtra wracamy do pierwszej strony.
      } else {
        // Po każdej operacji (usunięcie, edycja, zmiana strony) liczba stron mogła
        // się zmienić — bieżąca strona musi zostać w prawidłowym zakresie. Bez tego
        // po usunięciu całej ostatniej strony zostawaliśmy na numerze, którego już
        // nie ma (pusta tabela i licznik „7 / 6").
        this.clampPageNumber();
      }
      this.$forceUpdate(); // Wymuszamy odświeżenie komponentu
    },
    // Przycina numer bieżącej strony do zakresu 1..totalPages. Wołane po każdym
    // przeliczeniu `totalCount`, więc widok nigdy nie zostaje na nieistniejącej stronie.
    clampPageNumber() {
      const page = Math.min(
        Math.max(parseInt(this.pageNumber, 10) || 1, 1),
        this.totalPages
      );

      if (page !== this.pageNumber) {
        this.pageNumber = page;
      }
    },
    // Zbiera unikalny, posortowany po nazwie zbiór grup list poszukiwanych występujących
    // wśród pobranych użytkowników i przekazuje go rodzicowi (do zasilenia selecta filtra).
    emitAvailableWantedGroups() {
      const byId = new Map();
      this.users.forEach((user) => {
        (user.wantedListContactGroups || []).forEach((g) => {
          if (!byId.has(g.id)) {
            byId.set(g.id, { id: g.id, name: g.name });
          }
        });
      });
      const groups = Array.from(byId.values()).sort((a, b) =>
        (a.name || "").localeCompare(b.name || "")
      );
      this.$emit("wantedGroupsLoaded", groups);
    },
    // Powrót z widoku edycji użytkownika do listy
    handleGoBack() {
      this.edit = false;
      this.toEdit = null;
      this.$emit('editMode', false);
      this.retrieveUsers(); // Po powrocie odśwież listę użytkowników
    },
    // Zmiana liczby użytkowników na stronie. Wracamy na pierwszą stronę, bo przy
    // większym rozmiarze bieżący numer mógłby wyjść poza zakres, a paginacja jest
    // klientowa — nowy podział nie wymaga zapytania do API.
    onPageSizeChange(event) {
      const nextPageSize = Number(event.target.value);

      if (!this.pageSizeOptions.includes(nextPageSize) || nextPageSize === this.pageSize) {
        return;
      }

      this.pageSize = nextPageSize;
      this.pageNumber = 1;
      this.applyFilters(true);
    },
    // Ustaw numer strony (z paginacji)
    setPage(event) {
      this.pageNumber = event;
      this.refreshData();
    },
    // Pobierz użytkowników z backendu (przy ładowaniu komponentu lub po powrocie z edycji)
    retrieveUsers() {
      this.loading = true;
      this.toDelete = [];
      this.toEdit = null;
      this.edit = false;
      this.refreshData();
    },
    // Pobierz i uaktualnij listę użytkowników (np. po zmianie strony)
    refreshData() {
      this.users = [];

      this.dataService
        .getUserList(this.pageNumber, this.pageSize)
        .then((data) => {
          // Kolejność ustala `applyFilters()` (sortowanie wg wybranej kolumny).
          this.users = Array.isArray(data.content) ? [...data.content] : [];

          this.emitAvailableWantedGroups();
          this.applyFilters(false);
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          console.log(error);
          this.showStatus("Nie udało się pobrać listy użytkowników.", "error");
        });
    },
    // Przejdź do widoku edycji użytkownika (po kliknięciu "Edytuj")
    setToEdit(id) {
      this.toDelete = [];
      this.edit = true;
      this.toEdit = id;
      this.$emit('editMode', true);
    },
    // Zaznacz lub odznacz użytkownika do usunięcia (checkbox).
    // `InputCheckbox` sam pilnuje swojego `disabled`, ale natywny listener `@click` w tym
    // szablonie i tak by się odpalił — dlatego warunek musi być też tutaj.
    setToDelete(id) {
      const user = this.users.find((it) => it.id === id);
      if (user && !this.canAdministerUser(user)) {
        return;
      }
      if (this.toDelete.includes(id)) {
        this.toDelete.splice(this.toDelete.indexOf(id), 1);
      } else {
        this.toDelete.push(id);
      }
    },
    // Usuń wszystkich zaznaczonych użytkowników (po kliknięciu "Usuń")
    deleteUsers() {
      Users.deleteUsers(this.toDelete)
        .then(() => {
          this.toDelete = [];
          this.$emit("clearSearch"); // Wyczyść zapytanie wyszukiwania (jeśli było)
          this.refreshData();
          this.showStatus("Użytkownicy zostali usunięci.", "success");
        })
        .catch((error) => {
          this.loading = false;
          console.log(error);
          this.showStatus("Nie udało się usunąć użytkowników.", "error");
        });
    },
    // Wymuś wylogowanie użytkownika — usuwa jego sesję z bazy danych.
    killSession(id, username) {
      if (!window.confirm(this.t('kill-session-confirm', { username: username || '' }))) {
        return;
      }

      Users.killUserSession(id)
        .then(() => {
          this.showStatus(this.t('session-killed'), "success");
          // Konto nie ma już sesji — lista musi to pokazać (przycisk „Wyloguj" gaśnie).
          this.refreshData();
        })
        .catch((error) => {
          console.log(error);
          this.showStatus(this.t('kill-session-error'), "error");
        });
    },
    // Wynik operacji pokazuje globalny komponent powiadomień (AppNotifications).
    showStatus(message, type = "error") {
      useNotificationStore().push(message, type);
    },
  },
};
</script>

<style scoped>
.content {
  width: 100%;
  min-height: 270px;
  margin-bottom: 20px;
}

h3 {
  margin: 0;
  padding: var(--spacing-md) var(--spacing-lg);
  background-color: var(--surface-secondary);
  border-bottom: 1px solid var(--border-default);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  color: var(--text-primary);
}

.actions {
  position: relative;
}

.pager {
  margin-bottom: 1rem;
  padding-bottom: 1.75rem;
  padding-top: 0.5rem;
}

.remove {
  position: absolute;
  right: 71px;
  top: 0;
}

/* Pasek nad tabelą z wyborem liczby użytkowników na stronie — wygląd taki sam
   jak na liście pojazdów (`VehicleList`). */
.user-list-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--spacing-md);
  padding: var(--spacing-md) 0;
  font-size: var(--font-size-sm);
}

/* Przycisk „Utwórz grupę" po lewej, wybór rozmiaru strony zostaje po prawej. */
.user-list-create-group {
  margin-right: auto;
  margin-left: 1rem;
  min-height: var(--control-height-sm);
}

.user-list-page-size-label {
  color: var(--text-secondary);
  font-weight: var(--font-weight-medium);
}

.user-list-page-size-select {
  min-width: 80px;
  min-height: var(--control-height-sm);
  padding: 0 var(--spacing-sm);
  border: 1px solid var(--border-default);
  border-radius: var(--control-radius);
  background-color: var(--surface-primary);
  color: var(--text-primary);
  margin-right: 1rem;
}

/* --- tabela użytkowników --------------------------------------------------
   Zestaw kolumn zależy od wariantu: 10 w SG/NOSG/POSG (z „Grupami
   poszukiwanych" i „Jednostką"), 8 w pozostałych (bez obu) —
   w kolumnie treści o szerokości ~1015 px
   (`--app-width` 1300 minus sidebar 285). Żeby wiersz zawsze był
   JEDNOLINIJKOWY, tabela ma:
   - `table-layout: fixed` + szerokości w procentach na klasach `cell-*`,
   - `white-space: nowrap` na komórkach danych (id nigdy nie schodzi do drugiej
     linii),
   - `overflow: hidden` + `text-overflow: ellipsis` — treść, która się nie
     mieści (najdłuższe e-maile), jest przycięta wielokropkiem, a nie wypycha
     kolumny ani nie nachodzi na sąsiednie (pełna wartość jest w `title`).
   Nagłówki są wyjątkiem: mogą się zawijać, bo etykiety typu „Grupy
   poszukiwanych" są dłuższe niż ich kolumny. */
.user-table {
  width: 100%;
  table-layout: fixed;
  font-size: var(--font-size-xs);
  color: var(--text-primary);
}

/* SG / NOSG / POSG: komplet kolumn („Grupy poszukiwanych" + „Jednostka"), więc
   typografia o stopień mniejsza i węższe pozostałe kolumny. */
.user-table--sg {
  font-size: var(--font-size-2xs);

  /* Suma = 100%. Kolumny dobrane pod najdłuższe realne treści przy 11 px:
     data logowania „2026-08-10 21:20:43" (~101 px), „ADMINISTRATOR" (~85 px),
     dwa przyciski akcji (~140 px, patrz `.user-actions .btn`). Nazwy grup są
     najdłuższe i przycinają się wielokropkiem — pełna wartość w `title`. */
  --w-lp: 4%;
  --w-name: 10%;
  --w-roles: 11%;
  --w-ugroup: 9%;
  --w-groups: 10%;
  --w-login: 12%;
  --w-firstname: 8%;
  --w-lastname: 9%;
  --w-unit: 7%;
  --w-actions: 16%;
  --w-select: 4%;
}

/* Pozostałe warianty (TEST, lokalny dev): ani grup poszukiwanych, ani „Jednostki" — tylko
   9 kolumn. Nadwyżka miejsca idzie do kolumn z tekstem, a NIE do „Akcji":
   ta ma stałą treść (dwa przyciski ~140 px), więc każdy dodatkowy procent
   zamieniał się w pustą przerwę między przyciskami a checkboxem. */
.user-table--plain {
  --w-lp: 5%;
  --w-name: 13%;
  --w-roles: 14%;
  --w-ugroup: 11%;
  --w-groups: 0;
  --w-login: 14%;
  --w-firstname: 11%;
  --w-lastname: 11%;
  --w-unit: 0;
  --w-actions: 17%;
  --w-select: 4%;
}

.user-table .cell-lp { width: var(--w-lp); }
.user-table .cell-name { width: var(--w-name); }
.user-table .cell-roles { width: var(--w-roles); }
.user-table .cell-ugroup { width: var(--w-ugroup); }
.user-table .cell-groups { width: var(--w-groups); }
.user-table .cell-login { width: var(--w-login); }
.user-table .cell-firstname { width: var(--w-firstname); }
.user-table .cell-lastname { width: var(--w-lastname); }
.user-table .cell-unit { width: var(--w-unit); }
.user-table .cell-actions { width: var(--w-actions); }
.user-table .cell-select { width: var(--w-select); }

/* Komórki danych: jedna linia, nadmiar ucięty wielokropkiem.
   Selektor z dwiema klasami tabeli bije reguły z `design-system.css`
   (`.content-wrap table.table tbody td`) bez `!important`. */
.user-table.table tbody td {
  padding: var(--spacing-sm);
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Nagłówki mogą się zawijać — `design-system.css` wymusza na nich `nowrap`,
   a przy stałych procentach dłuższe etykiety wychodziły jedna na drugą. */
.user-table.table thead th {
  padding: var(--spacing-sm);
  white-space: normal;
}

/* Numer porządkowy: węższy padding, żeby 4-cyfrowe id zmieściło się w 4%. */
.user-table.table tbody td.cell-lp {
  padding-left: var(--spacing-xs);
  padding-right: var(--spacing-xs);
}

/* Nagłówki sortowalne (SG/NOSG/POSG): klik zmienia kolumnę/kierunek sortowania. */
.user-table.table thead th.cell-sortable {
  cursor: pointer;
  user-select: none;
}

.user-table.table thead th.cell-sortable:hover {
  background-color: var(--surface-hover);
}

.sort-indicator {
  margin-left: 2px;
  color: var(--text-secondary);
  font-size: 0.85em;
}

.cell-sorted .sort-indicator {
  color: var(--text-primary);
}

/* Nagłówek „Akcja" idzie za wyśrodkowaną parą przycisków pod nim. */
.user-table.table thead th.cell-actions {
  text-align: center;
}

/* Checkbox wyśrodkowany w swojej kolumnie — bez dobieranego na oko paddingu. */
.user-table.table tbody td.cell-select {
  overflow: visible;
  text-align: center;
}

/* `nowrap` — przyciski nigdy nie stają jeden pod drugim; jeśli tłumaczenie
   etykiet nie mieści się w kolumnie, ucina je `overflow` komórki.
   `justify-content: center` wyśrodkowuje parę przycisków w kolumnie: przy
   ośmiu kolumnach („Akcja" jest wtedy szersza niż jej treść) resztka miejsca
   rozkłada się po obu stronach zamiast zbierać się w jedną przerwę przed
   checkboxem. */
.user-actions {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
}

/* Kompaktowy rozmiar przycisków w wierszu tabeli. Domyślny z `button-system.css`
   (36 px wysokości, 16 px paddingu, 13 px fontu) dawał razem ~230 px i nie
   mieścił się w kolumnie „Akcja" — przyciski wychodziły na kolumnę z checkboxem
   i poza prawą krawędź tabeli. Tu ~140 px, czyli z zapasem w 18%.
   Selektor z rodzicem bije reguły jednoklasowe z `button-system.css`, więc
   nie potrzebuje `!important`. */
.user-actions .btn {
  min-height: 26px;
  padding: 0 var(--spacing-sm);
  font-size: var(--font-size-2xs);
  border-radius: 4px;
  white-space: nowrap;
}

.button-edit:hover {
  background-color: var(--status-warn-strong) !important;
  color: white !important;
}

.button-delete {
  padding: 6px 18px 6px 18px;
  margin-top: 9px;
  border-radius: 4px;
}

.button-delete:hover {
  background-color: var(--status-error) !important;
  color: white !important;
}

.button-delete span {
  margin-top: 3px;
}
</style>
